import type { DueEntryT, EntryViewT, StatefulTrackerViewT } from "@patient-watch/types";

import { scheduleStepDisplayLabel } from "./timingLabelDerivation";

export type ScheduleSlotStatus = "current" | "empty" | "occupied";

export interface EntryScheduleSlot {
    schedule_index: number;
    label: string;
    due_at: string | null;
    schedule_id: string;
    timing_id: string;
    interval: number;
    time_unit: string;
    status: ScheduleSlotStatus;
    occupied_entry_id?: string;
}

type ScheduleContainer = StatefulTrackerViewT;

function occupiedByIndex(entries: EntryViewT[], currentEntryId: string): Map<number, string> {
    const map = new Map<number, string>();
    for (const e of entries) {
        if (e.schedule_index == null || !e.entry_id || e.entry_id === currentEntryId) continue;
        map.set(e.schedule_index, e.entry_id);
    }
    return map;
}

function slotFromDue(
    due: NonNullable<DueEntryT>,
    status: ScheduleSlotStatus,
    occupied_entry_id?: string
): EntryScheduleSlot {
    return {
        schedule_index: due.schedule_index,
        label: due.label,
        due_at: due.due_at ?? null,
        schedule_id: due.schedule_id,
        timing_id: due.timing_id,
        interval: due.interval,
        time_unit: due.time_unit,
        status,
        occupied_entry_id,
    };
}

function statusForIndex(
    scheduleIndex: number,
    currentEntryId: string,
    currentEntryIndex: number | null | undefined,
    occupied: Map<number, string>
): { status: ScheduleSlotStatus; occupied_entry_id?: string } {
    if (currentEntryIndex === scheduleIndex) {
        return { status: "current" };
    }
    const otherId = occupied.get(scheduleIndex);
    if (otherId) {
        return { status: "occupied", occupied_entry_id: otherId };
    }
    return { status: "empty" };
}

/** Build selectable schedule steps for moving a completed entry to another slot. */
export function buildEntryScheduleSlots(
    container: ScheduleContainer,
    currentEntryId: string,
    currentScheduleIndex: number | null | undefined
): EntryScheduleSlot[] {
    const entries = container.entries_view ?? [];
    const occupied = occupiedByIndex(entries, currentEntryId);

    const processed = (container.processed_schedule ?? []).filter(
        (s): s is NonNullable<DueEntryT> => s != null && typeof s.schedule_index === "number"
    );

    if (processed.length > 0) {
        return processed
            .slice()
            .sort((a, b) => a.schedule_index - b.schedule_index)
            .map((slot) => {
                const { status, occupied_entry_id } = statusForIndex(
                    slot.schedule_index,
                    currentEntryId,
                    currentScheduleIndex,
                    occupied
                );
                return slotFromDue(slot, status, occupied_entry_id);
            });
    }

    const upcoming =
        "upcoming_entries" in container
            ? ((container.upcoming_entries ?? []).filter(Boolean) as NonNullable<DueEntryT>[])
            : [];

    const combined: NonNullable<DueEntryT>[] = [
        ...entries
            .filter((e) => e.schedule_index != null && e.schedule_id)
            .map((e) => {
                const idx = e.schedule_index!;
                const row = e as EntryViewT & { timing_id?: string | null };
                return {
                    label: scheduleStepDisplayLabel({
                        timing_label: e.timing_label,
                        timing_interval: e.timing_interval,
                        time_unit: e.time_unit,
                        schedule_index: idx,
                    }),
                    schedule_index: idx,
                    schedule_id: e.schedule_id!,
                    timing_id: row.timing_id ?? "",
                    interval: Number(e.timing_interval) || 0,
                    time_unit: (e.time_unit ?? "day") as NonNullable<DueEntryT>["time_unit"],
                    due_at: e.calculated_entry_time ?? e.entry_time ?? "",
                    is_locked: false,
                } satisfies NonNullable<DueEntryT>;
            }),
        ...((container.due_entries ?? []).filter(Boolean) as NonNullable<DueEntryT>[]),
        ...upcoming,
    ];

    const byIndex = new Map<number, NonNullable<DueEntryT>>();
    for (const item of combined) {
        if (!byIndex.has(item.schedule_index)) {
            byIndex.set(item.schedule_index, item);
        }
    }

    return Array.from(byIndex.values())
        .sort((a, b) => a.schedule_index - b.schedule_index)
        .map((slot) => {
            const { status, occupied_entry_id } = statusForIndex(
                slot.schedule_index,
                currentEntryId,
                currentScheduleIndex,
                occupied
            );
            return slotFromDue(slot, status, occupied_entry_id);
        });
}

export function isScheduledEntry(entry: EntryViewT): boolean {
    return entry.schedule_index != null;
}
